import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';

import {
  getLiveChildTranscript,
  subscribeAnyLiveChildTranscript,
} from './liveChildTranscripts';

/** Nested answer words the app can see (a child transcript, a bridged session) and this package cannot. */
export interface NestedAnswerTextSource {
  read: (items: readonly ChatItem[]) => boolean;
  subscribe: (onChange: () => void) => () => void;
}

/** The slice of a bridged Evidence session entry that can carry answer words. */
export interface NestedAnswerTimelineEntry {
  type?: string;
  content?: unknown;
}

export interface NestedAnswerTextSources {
  childTranscript: (childAgentRunId: string) => readonly ChatItem[] | undefined;
  /** The step itself, so a reload can read the session saved on its result. */
  evidenceTimeline: (
    tool: ToolUseItem
  ) => readonly NestedAnswerTimelineEntry[] | undefined;
}

const transcriptHasAnswerText = (items: readonly ChatItem[]): boolean =>
  items.some(
    (item) =>
      item.kind === 'assistant_text' &&
      typeof item.text === 'string' &&
      item.text.trim().length > 0
  );

const timelineHasAnswerText = (
  timeline: readonly NestedAnswerTimelineEntry[] | undefined
): boolean =>
  timeline?.some(
    (entry) =>
      entry.type === 'text_item' &&
      typeof entry.content === 'string' &&
      entry.content.trim().length > 0
  ) ?? false;

// Any user message opens the turn, bubble or not: the same boundary as
// `deriveTurnLifecycle`, which keys the reply wait.
const itemsAfterLatestUserMessage = (items: readonly ChatItem[]): readonly ChatItem[] => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (items[index]?.kind === 'user_message') return items.slice(index + 1);
  }
  return [];
};

/**
 * True once the latest turn's Evidence step has painted answer words, either
 * in a child-run transcript or in the bridged Evidence session for that tool.
 * Tool rows and search chunks do not count. Earlier turns are ignored.
 */
export const latestTurnNestedAnswerHasText = (
  items: readonly ChatItem[],
  sources: NestedAnswerTextSources
): boolean => {
  for (const item of itemsAfterLatestUserMessage(items)) {
    if (item.kind !== 'tool_use') continue;
    if (item.childAgentRunId) {
      const transcript = sources.childTranscript(item.childAgentRunId);
      if (transcript && transcriptHasAnswerText(transcript)) return true;
      continue;
    }
    if (timelineHasAnswerText(sources.evidenceTimeline(item))) return true;
  }
  return false;
};

/**
 * Child transcripts are published into this package's registry; the bridged
 * Evidence sessions are app state, so the app supplies their read and their
 * change feed. Build once at module scope: the hook subscribes by identity.
 */
export const createNestedAnswerTextSource = ({
  evidenceTimeline,
  subscribeEvidenceTimelines,
}: {
  evidenceTimeline: NestedAnswerTextSources['evidenceTimeline'];
  subscribeEvidenceTimelines: (onChange: () => void) => () => void;
}): NestedAnswerTextSource => ({
  read: (items) =>
    latestTurnNestedAnswerHasText(items, {
      childTranscript: getLiveChildTranscript,
      evidenceTimeline,
    }),
  subscribe: (onChange) => {
    const unsubscribeChildren = subscribeAnyLiveChildTranscript(onChange);
    const unsubscribeSessions = subscribeEvidenceTimelines(onChange);
    return () => {
      unsubscribeChildren();
      unsubscribeSessions();
    };
  },
});
